import { Component } from '@theme/component';

class TintSimulator extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.simulatorAbort?.abort();
    super.disconnectedCallback();
  }

  setPreview(configuration) {
    const current = this.simulatorState ?? {film:'carbon',front:35,rear:15,lighting:'day',clear:false,view:'outside'};
    const film = configuration.film === 'ceramic' ? 'ceramic' : 'carbon';
    const allowed = film === 'ceramic' ? [5,15,35,50] : [5,15,35];
    const shade = (value, fallback) => allowed.includes(Number(value)) ? Number(value) : fallback;
    this.simulatorState = {...current,film,front:shade(configuration.front,35),rear:shade(configuration.rear,15),clear:false};
    if (this.isConnected) this.initialize();
  }

  initialize() {
    this.simulatorAbort?.abort();
    const controller = new AbortController();
    this.simulatorAbort = controller;
    const root = this;
    const q = (id) => root.querySelector('[data-sim-id="' + id + '"]');
    const config = JSON.parse(root.querySelector('[data-sim-config]').textContent);
    const text = config.strings;
    const state = this.simulatorState ?? {film:'carbon',front:35,rear:15,lighting:'day',clear:false,view:'outside'};
    this.simulatorState = state;
    const scene = root.querySelector('.otk-sim__scene');
    const controls = root.querySelector('.otk-sim__controls');
    const orderLink = root.querySelector('.otk-sim__order');
    const photo = q('scene-photo');
    const loading = root.querySelector('.otk-sim__loading-indicator');
    const vlt = {carbon:{5:5,15:16,35:36},ceramic:{5:5,15:16,35:34,50:50}};
    const cache = new Map();
    let request = 0;
    const table = (transmission) => {
      if (cache.has(transmission)) return cache.get(transmission);
      const values = Array.from({length:256}, (_,i) => {
        const encoded = i / 255;
        const light = encoded <= 0.04045 ? encoded / 12.92 : Math.pow((encoded + 0.055) / 1.055, 2.4);
        const output = light * transmission;
        return (output <= 0.0031308 ? 12.92 * output : 1.055 * Math.pow(output, 1 / 2.4) - 0.055).toFixed(7);
      }).join(' ');
      cache.set(transmission, values);
      return values;
    };
    const loadPhoto = () => {
      const current = ++request;
      const source = config.images[state.view + '-' + state.lighting];
      if (photo.getAttribute('href') === source) { loading.hidden = true; return; }
      loading.textContent = text.loading;
      loading.hidden = false;
      const image = new Image();
      image.src = source;
      image.decode().then(() => {
        if (controller.signal.aborted || current !== request) return;
        photo.setAttribute('href', source);
        loading.hidden = true;
      }).catch(() => {
        if (!controller.signal.aborted && current === request) loading.textContent = text.load_error;
      });
    };
    const update = (message = '') => {
      controls.dataset.film = state.film;
      controls.dataset.view = state.view;
      root.querySelector('.otk-sim__visual').dataset.view = state.view;
      scene.dataset.lighting = state.lighting;
      scene.dataset.view = state.view;
      scene.dataset.clear = String(state.clear);
      for (const group of ['front','rear']) {
        const transmission = state.clear ? 1 : vlt[state.film][state[group]] / 100;
        q('transmission-' + group).dataset.transmission = String(transmission);
        q('transmission-' + group).querySelectorAll('feFuncR,feFuncG,feFuncB').forEach(channel => channel.setAttribute('tableValues', table(transmission)));
        root.querySelectorAll('[data-group="' + group + '"] button').forEach(button => {
          button.disabled = state.film === 'carbon' && button.dataset.shade === '50';
          button.setAttribute('aria-pressed', String(Number(button.dataset.shade) === state[group]));
        });
        q(group + '-output').textContent = state[group] + '%';
        const readout = q(group + '-readout');
        const film = document.createElement('span');
        film.textContent = text[state.film];
        readout.replaceChildren(document.createTextNode(state[group] + '% '), film);
      }
      root.querySelectorAll('.otk-sim__film-switch button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.film === state.film)));
      root.querySelectorAll('button[data-light]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.light === state.lighting)));
      root.querySelectorAll('button[data-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.view === state.view)));
      q('view-caption').textContent = text[state.view + '_caption'];
      q('compare').setAttribute('aria-pressed', String(state.clear));
      q('compare').textContent = state.clear ? text.return_tint : text.clear;
      const summary = text[state.film] + ' · ' + text[state.view] + ' · ' + text.front + ' ' + state.front + '%' + (state.view === 'outside' ? ' · ' + text.rear + ' ' + state.rear + '%' : '') + ' · ' + text[state.lighting];
      q('car-description').textContent = state.clear ? text.clear_status : summary;
      q('preview-status').textContent = message || (state.clear ? text.clear_status : summary);
      if (orderLink) {
        const destination = new URL(orderLink.href);
        destination.searchParams.set('film', state.film);
        destination.searchParams.set('front', String(state.front));
        destination.searchParams.set('rear', String(state.rear));
        orderLink.href = destination.href;
      }
      loadPhoto();
    };
    root.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const film = event.target.closest('.otk-sim__film-switch button');
      if (film) {
        state.film = film.dataset.film;
        let adjusted = false;
        if (state.film === 'carbon') for (const group of ['front','rear']) if (state[group] === 50) { state[group] = 35; adjusted = true; }
        state.clear = false;
        update(adjusted ? text.carbon_adjusted : '');
        return;
      }
      const shade = event.target.closest('[data-shade]');
      if (shade && !shade.disabled) { state[shade.closest('[data-group]').dataset.group] = Number(shade.dataset.shade); state.clear = false; update(); return; }
      const lighting = event.target.closest('button[data-light]');
      if (lighting) { state.lighting = lighting.dataset.light; update(); return; }
      const view = event.target.closest('button[data-view]');
      if (view) { state.view = view.dataset.view; update(); return; }
      if (event.target.closest('[data-sim-id="compare"]')) { state.clear = !state.clear; update(); return; }
      if (event.target.closest('[data-sim-id="reset"]')) { Object.assign(state,{film:'carbon',front:35,rear:15,lighting:'day',clear:false,view:'outside'}); update(); }
    }, {signal:controller.signal});
    update();
  }
}

if (!customElements.get('otk-tint-simulator-component')) customElements.define('otk-tint-simulator-component', TintSimulator);
